'use client';

import { Loader2, Lock } from 'lucide-react';
import Link from 'next/link';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface FeatureToggleRowProps {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  locked?: boolean;
  lockedBadge?: string;
  lockedLinkLabel?: string;
  lockedHref?: string;
  onLockedClick?: () => void | Promise<void>;
  lockedPending?: boolean;
  children?: React.ReactNode;
}

export function FeatureToggleRow({
  id,
  title,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  locked = false,
  lockedBadge,
  lockedLinkLabel,
  lockedHref = '/pricing',
  onLockedClick,
  lockedPending = false,
  children,
}: FeatureToggleRowProps) {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2">
            <Label htmlFor={id} className="text-base">
              {title}
            </Label>
            {locked && lockedBadge && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                <Lock className="h-3 w-3" />
                {lockedBadge}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
          {locked &&
            lockedLinkLabel &&
            (onLockedClick ? (
              <button
                type="button"
                onClick={() => void onLockedClick()}
                disabled={lockedPending}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {lockedPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {lockedLinkLabel} →
              </button>
            ) : (
              <Link
                href={lockedHref}
                className="inline-block text-sm text-primary hover:underline"
              >
                {lockedLinkLabel} →
              </Link>
            ))}
        </div>
        <Switch
          id={id}
          checked={!locked && checked}
          disabled={disabled || locked}
          onCheckedChange={onCheckedChange}
        />
      </div>
      {children}
    </div>
  );
}
